'use client';

import { memo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { Pen, Trash } from '@zoomit/icons';
import { Col, Flex, IconButton, Spacing } from '@zoomit/primitives';
import { Checkbox, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { useEditBookmark } from '../../hooks/useBookmarksManagement';
import type { BookmarkEditFormType, BookmarkItemType } from '../../types/articles';
import ArticleBookmarkEditForm from './ArticleBookmarkEditForm';

type Props = BookmarkItemType & {
  openConfirmModal: () => void;
};

const ArticleBookmarkItem = (props: Props) => {
  const { name, pending = false, id, openConfirmModal } = props;
  const [showForm, setShowForm] = useState(false);
  const { register } = useFormContext();
  const formMethods = useForm({
    defaultValues: {
      name,
      id,
    },
  });
  const { handleSubmit } = formMethods;
  const editBookmarkMutation = useEditBookmark({ onSuccess: () => setShowForm(false) });

  return (
    <Flex className="w-full" justifyContent="space-between" alignItems="center">
      {!showForm && (
        <>
          <Col size={9} md={10}>
            <Checkbox
              variant="lg"
              id={id}
              {...register(`lists.${id}`)}
              label={
                <Typography variant="callout-bold" textColor={cssVar(theme.text)}>
                  {name}
                </Typography>
              }
            />
          </Col>
          <Col size={3} md={2}>
            <Flex gap={Spacing.x4} justifyContent="flex-end" alignItems="center">
              <IconButton disabled={pending} onClick={() => setShowForm(!showForm)}>
                <Pen size="md" fill={cssVar(theme.grey1)} />
              </IconButton>
              <IconButton disabled={pending} onClick={() => openConfirmModal()}>
                <Trash fill={cssVar(theme.grey1)} size="md" />
              </IconButton>
            </Flex>
          </Col>
        </>
      )}
      {showForm && (
        <form
          onSubmit={handleSubmit((data: BookmarkEditFormType) => {
            if (data.name === name) {
              setShowForm(false);
              return;
            }
            editBookmarkMutation.mutate(data);
          })}
          className="w-full"
        >
          <FormProvider {...formMethods}>
            <ArticleBookmarkEditForm closeForm={() => setShowForm(false)} />
          </FormProvider>
        </form>
      )}
    </Flex>
  );
};

export default memo(ArticleBookmarkItem);
